import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import {
  createChangeOfBrokerRequest,
  getChangeOfBrokerOptions,
  getChangeOfBrokerRequest,
  getChangeOfBrokerDetailedRequest,
  getCOBStats,
  getCOBClientBreakdown,
  updateCobStatus,
} from "../../api/distributor";
import { COBClientDetail, PaginatedResponse } from "../../types";

export const cobKeys = {
  all: ["change-of-broker"] as const,
  request: (clientId: number) => [...cobKeys.all, { clientId }] as const,
  detailed: (clientId: number) =>
    [...cobKeys.all, "detailed", { clientId }] as const,
  options: (investmentAccountId: string) =>
    [...cobKeys.all, "options", { investmentAccountId }] as const,
  stats: () => [...cobKeys.all, "stats"] as const,
  breakdown: {
    all: ["change-of-broker", "breakdown"] as const,
    filtered: (variables: {
      page: number;
      pageSize: number;
      status?: string[];
    }) => [...cobKeys.breakdown.all, { ...variables }] as const,
  },
};

export const cobRequestQueryOptions = (clientId: number) =>
  queryOptions({
    queryKey: cobKeys.request(clientId),
    queryFn: () => getChangeOfBrokerRequest(clientId),
  });

export const cobDetailedQueryOptions = (clientId: number) =>
  queryOptions({
    queryKey: cobKeys.detailed(clientId),
    queryFn: () => getChangeOfBrokerDetailedRequest(clientId),
  });

export const cobOptionsQueryOptions = (
  investmentAccountId: string,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  queryOptions({
    queryKey: cobKeys.options(investmentAccountId),
    queryFn: () => getChangeOfBrokerOptions({ investmentAccountId }),
    enabled: enabled && !!investmentAccountId,
  });

export const cobStatsQueryOptions = () =>
  queryOptions({
    queryKey: cobKeys.stats(),
    queryFn: () => getCOBStats(),
    placeholderData: (previousData) => previousData,
  });

export const cobBreakdownQueryOptions = (variables: {
  page: number;
  pageSize: number;
  status?: string[];
}) =>
  queryOptions({
    queryKey: cobKeys.breakdown.filtered(variables),
    queryFn: () => getCOBClientBreakdown(variables),
    placeholderData: (previousData) => previousData,
  });

export function useChangeOfBrokerMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createChangeOfBrokerRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cobKeys.all });
    },
  });
}

export function useGetChangeOfBrokerRequest(clientId: number) {
  return useQuery(cobRequestQueryOptions(clientId));
}

export function useGetChangeOfBrokerDetailedRequest(clientId: number) {
  return useQuery(cobDetailedQueryOptions(clientId));
}

export function useChangeOfBrokerOptions(
  investmentAccountId: string,
  options?: { enabled?: boolean }
) {
  return useQuery(cobOptionsQueryOptions(investmentAccountId, options));
}

export const useCOBStats = () => {
  return useQuery(cobStatsQueryOptions());
};

export const useCOBClientBreakdown = (variables: {
  page: number;
  pageSize: number;
  status?: string[];
}) => {
  return useQuery(cobBreakdownQueryOptions(variables));
};

export const useUpdateCobStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCobStatus,
    onSuccess: (_data, variables) => {
      queryClient.setQueriesData<PaginatedResponse<COBClientDetail>>(
        { queryKey: cobKeys.breakdown.all },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            results: old.results.map((client) => {
              if (
                client.investment_account_id !== variables.investmentAccountId
              )
                return client;
              return {
                ...client,
                folio_details: client.folio_details.map((folio) =>
                  variables.folioIds.includes(folio.folio)
                    ? { ...folio, status: variables.status }
                    : folio
                ),
              };
            }),
          };
        }
      );
    },
  });
};

export const useInvalidateCobBreakdown = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: cobKeys.breakdown.all });
  }, [queryClient]);
};
